import React, { useCallback, useEffect, useState } from 'react'
import type { ClientLibraryId } from 'types/client-library'
import type { TrackId } from 'types/track'
import api from '@disco/api/server'
import { formatList } from '@disco/utils/string-utils'
import LoadingIndicator from 'components/common/copy-metadata-track/loading-indicator'
import Link from 'components/ui/link'
import cn from 'clsx'
import styles from './LibraryMembershipInfo.module.css'

type State = {
  albums: string[]
  groups: string[]
  playlists: string[]
}
type Props = {
  libraryId: ClientLibraryId
  trackId: TrackId
}

/**
 * Library membership info component that displays why a track is included in a catalog
 * Shows the albums, folders, and playlists that caused the track to be added to the catalog
 * @param libraryId - ID of the client library/catalog
 * @param trackId - ID of the track to analyze
 */
const LibraryMembershipInfo = ({ libraryId, trackId }: Props) => {
  const [error, setError] = useState<string | null | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [state, setState] = useState<State>({
    albums: [],
    groups: [],
    playlists: [],
  })
  const loadData = useCallback(async () => {
    try {
      const response = await api.diagnoseLibraryMembership(libraryId, trackId)
      setState({
        albums: response.albums.map(item => `‘${item.name}’`),
        groups: response.groups.map(items =>
          items.length === 1
            ? `‘${items[0].name}’`
            : `‘${items[items.length - 1].name}’ (${items
                .map(item => item.name)
                .join('/')})`,
        ),
        playlists: response.playlists.map(item => `‘${item.name}’`),
      })
    } catch (error) {
      setError('An error occurred while loading data.')
    }

    setLoading(false)
  }, [])
  useEffect(() => {
    setLoading(true)
    loadData()
  }, [])

  const renderInfo = ({ data, entity, link }): React.ReactNode => {
    return data.length === 1 ? (
      <li>
        the {entity[0]} {formatList(data)}, which is added to the Catalog in{' '}
        {link}
      </li>
    ) : (
      <li>
        the {entity[1]} {formatList(data)}, which are added to the Catalog in{' '}
        {link}
      </li>
    )
  }

  return loading ? (
    <div
      className={cn(
        styles.libraryMembershipInfo,
        styles.libraryMembershipInfoLoading,
      )}
    >
      <LoadingIndicator size="medium" />
    </div>
  ) : (
    <div className={styles.libraryMembershipInfo}>
      {error ?? (
        <>
          This track is in this Catalog because it’s in:
          <ul>
            {state.groups.length > 0 &&
              renderInfo({
                data: state.groups,
                entity: ['folder', 'folders'],
                link: (
                  <Link to={`/catalogs/${libraryId}/content`}>Content</Link>
                ),
              })}
            {state.playlists.length > 0 &&
              renderInfo({
                data: state.playlists,
                entity: ['playlist', 'playlists'],
                link: (
                  <Link to={`/catalogs/${libraryId}/playlists`}>Playlists</Link>
                ),
              })}
            {state.albums.length > 0 &&
              renderInfo({
                data: state.albums,
                entity: ['album', 'albums'],
                link: <Link to={`/catalogs/${libraryId}/albums`}>Albums</Link>,
              })}
          </ul>
        </>
      )}
    </div>
  )
}

export default React.memo<Props>(LibraryMembershipInfo)
